import React, { useMemo } from "react";
import { css } from "@linaria/core";
import t from "../../../../../../../../../../i18n";
import { spacings } from "@ui/Theme";
import { color } from "@ui";
import { type FailureValueStatus } from "../../../../../../../../../../api/useFailureMetadata";
import formatDate from "@ui/i18n/formatDate";
import type { Period } from "@ui/Highcharts/hooks/usePeriod";
import Text from "@ui/Text";
import classNames from "classnames";
import { ListCheck } from "@ui/IconV2/react";
import Modal from "@ui/Modal";
import { useModals } from "@ui/lib/hooks/useModal";
import ModalFormBatchFaultStatusChange, {
  checkIfAllDetectionsAreFalse,
} from "../../../../../../../components/TransactionalModals/ModalFormBatchFaultStatusChange";
import type { FaultEvent } from "../../../../../../../../../../api/useFaultEvents";
import type { Project } from "../../../../../../../../../../api/useProjects";
import useToasts from "@ui/Toast/useToasts";
import { useFailureValuesMutation } from "../../../../../../../../Diagnosis/useFailureStatusMutation";
import { usePut } from "../../../../../../../../../../api/useCall";
import {
  generateFaultEventPayload,
  getNewDiagnosesStatus,
  isFailureValueInFaultEventOccurrencePeriods,
} from "diagnostics-domain/faultEvent";
import Button from "@ui/Button";
import Card from "./Card";

const FAULT_STATUS_EXECUTION_ORDER: FailureValueStatus[] = [
  "TO_BE_ANALYSED",
  "SUSPECTED",
  "CONFIRMED",
  "LEGACY_REJECTED",
  "REJECTED",
];
const FAULT_STATUS_CHANGE_MODAL = "fault-status-change" as const;

/**
 *
 * @param timeZone: project timezone
 * @param period: visible period graph
 * @param analysesGroupedByFaultStatus: Number of analyses by fault status
 *
 */
const BatchFaultStatusChange = ({
  project,
  event,
  period,
  className,
}: {
  project: Project;
  event: FaultEvent;
  period: Period;
  className?: string;
}) => {
  const { addToast } = useToasts();
  const mutateFaultsStatus = useFailureValuesMutation(project);
  const mutateFaultsEvent = usePut("/projects/:projectId(uuid)/faultEvents");

  const { modal, openModal, closeModal } = useModals<string>();

  const startPeriodString = formatDate(new Date(period.from), "slash", project.timezone);
  const endPeriodString = formatDate(new Date(period.to), "slash", project.timezone);

  const analysesGroupedByFaultStatus = useMemo(() => {
    const result: {
      inZoom: {
        [faultStatus in FailureValueStatus]: { date: number; failureName: string }[];
      };
      offZoom: {
        [faultStatus in FailureValueStatus]: { date: number; failureName: string }[];
      };
    } = {
      inZoom: {
        TO_BE_ANALYSED: [],
        BACK_TO_BE_ANALYSED: [],
        SUSPECTED: [],
        CONFIRMED: [],
        LEGACY_REJECTED: [],
        REJECTED: [],
      },
      offZoom: {
        TO_BE_ANALYSED: [],
        BACK_TO_BE_ANALYSED: [],
        SUSPECTED: [],
        CONFIRMED: [],
        LEGACY_REJECTED: [],
        REJECTED: [],
      },
    };
    for (const fault of event.metadata.faults) {
      for (const analysis of fault.statusByAnalysis) {
        if (
          !isFailureValueInFaultEventOccurrencePeriods(event, {
            projectId: event.projectId,
            failureName: fault.name,
            date: analysis.date,
          })
        )
          continue;

        const zoom =
          analysis.date >= period.from && analysis.date <= period.to ? "inZoom" : "offZoom";
        result[zoom][analysis.status].push({
          date: analysis.date,
          failureName: fault.name,
        });
      }
    }

    return result;
  }, [event, period.from, period.to]);

  const totalAnalysesCount = React.useMemo(
    () =>
      Object.values(analysesGroupedByFaultStatus["inZoom"]).reduce(
        (acc, analyses) => (acc += analyses.length),
        0,
      ) +
      Object.values(analysesGroupedByFaultStatus["offZoom"]).reduce(
        (acc, analyses) => (acc += analyses.length),
        0,
      ),
    [analysesGroupedByFaultStatus],
  );
  const hasAnyDetections = useMemo(
    () =>
      Object.values(analysesGroupedByFaultStatus["inZoom"]).some(analyses => analyses.length > 0),
    [analysesGroupedByFaultStatus],
  );

  const [currentFaultStatus, setCurrentFaultStatus] = React.useState<FailureValueStatus | null>(
    null,
  );

  const handleBatchFaultStatusChange = React.useCallback(
    async ({
      currentFaultStatus,
      newFaultStatus,
      applyToNewDetection,
    }: {
      currentFaultStatus: FailureValueStatus;
      newFaultStatus: FailureValueStatus;
      applyToNewDetection: boolean;
    }) => {
      const areAllDetectionsFalse = checkIfAllDetectionsAreFalse(
        analysesGroupedByFaultStatus,
        currentFaultStatus,
        newFaultStatus,
        totalAnalysesCount,
      );

      const isTransitionToClosedFaultEventStatus = areAllDetectionsFalse;
      const isTransitionToInProgressFaultEventStatus =
        event.metadata.status === "TO_BE_ANALYSED" && !areAllDetectionsFalse;
      const status = isTransitionToClosedFaultEventStatus
        ? "CLOSED"
        : isTransitionToInProgressFaultEventStatus
          ? "ONGOING"
          : event.metadata.status;
      const closedAt = isTransitionToClosedFaultEventStatus ? new Date() : event.closedAt;
      const reparability = isTransitionToClosedFaultEventStatus ? null : event.reparability;

      const faultEvent = generateFaultEventPayload({
        faultEvent: {
          ...event,
          detection: isTransitionToClosedFaultEventStatus ? false : null,
          closedAt,
          identifiedAction: status === "ACTION_IDENTIFIED",
          reparability,
          newDiagnosesStatus: applyToNewDetection
            ? getNewDiagnosesStatus(newFaultStatus)
            : event.newDiagnosesStatus,
        },
      });

      try {
        await mutateFaultsEvent({
          body: faultEvent,
          routeParams: { projectId: project.id },
        });

        const impactedAnalysesByStatusChange =
          analysesGroupedByFaultStatus["inZoom"][currentFaultStatus] ?? [];
        await mutateFaultsStatus(impactedAnalysesByStatusChange, {
          status: newFaultStatus,
        });
        addToast(
          "success",
          t(
            "project.fault_event.evolution_chart.batch_status_change.modal.form.change_status.toast.update_status",
          ),
        );
      } catch (e) {
        addToast("error", t("errors.backend_error"));
      }
      return true;
    },
    [
      addToast,
      project,
      event,
      mutateFaultsEvent,
      mutateFaultsStatus,
      analysesGroupedByFaultStatus,
      totalAnalysesCount,
    ],
  );

  const handleButton = React.useCallback(
    async (faultStatus: FailureValueStatus) => {
      if (faultStatus === "REJECTED") {
        await handleBatchFaultStatusChange({
          currentFaultStatus: faultStatus,
          newFaultStatus: "BACK_TO_BE_ANALYSED",
          applyToNewDetection: false,
        });
      } else {
        openModal(FAULT_STATUS_CHANGE_MODAL);
        if (analysesGroupedByFaultStatus["inZoom"][faultStatus].length === 0)
          setCurrentFaultStatus(null);
        else setCurrentFaultStatus(faultStatus);
      }
    },
    [analysesGroupedByFaultStatus, openModal, handleBatchFaultStatusChange],
  );

  return (
    <>
      <div className={classNames(className, batchFaultStatusChange)}>
        <span className={title}>
          <ListCheck color={color.carbon800} />
          <Text>
            {t("project.fault_event.evolution_chart.batch_status_change.displayed_period.title", {
              period: (
                <Text
                  type="body1SemiBold"
                  as="span"
                >{`${startPeriodString} - ${endPeriodString}`}</Text>
              ),
            })}
          </Text>
        </span>

        <div className={content}>
          <Text type="body2" className={description}>
            {t(
              "project.fault_event.evolution_chart.batch_status_change.displayed_period.description",
            )}
          </Text>
          <div className={actionFaultValueStatus}>
            {hasAnyDetections ? (
              FAULT_STATUS_EXECUTION_ORDER.map(faultStatusExecutionOrder => {
                const buttonName =
                  faultStatusExecutionOrder === "REJECTED"
                    ? t("project.fault_event.evolution_chart.batch_status_change.reintegrate")
                    : t("project.fault_event.evolution_chart.batch_status_change.change");

                return (
                  <Card
                    key={faultStatusExecutionOrder}
                    projectId={project.id}
                    event={event}
                    currentFaultStatus={faultStatusExecutionOrder}
                    analysesGroupedByFaultStatus={analysesGroupedByFaultStatus}
                    handleSubmit={handleBatchFaultStatusChange}
                    actionButton={
                      <Button
                        variant="primary"
                        disabled={
                          analysesGroupedByFaultStatus["inZoom"][faultStatusExecutionOrder]
                            .length === 0
                        }
                        onClick={() => handleButton(faultStatusExecutionOrder)}
                        slim
                      >
                        {buttonName}
                      </Button>
                    }
                  />
                );
              })
            ) : (
              <Text className={no_detection} type="body2">
                {t("project.fault_event.evolution_chart.batch_status_change.no_detection")}
              </Text>
            )}
          </div>
        </div>
      </div>

      {currentFaultStatus === null ? null : (
        <Modal
          title={t(
            "project.fault_event.evolution_chart.batch_status_change.modal.change_status.title",
          )}
          isOpen={modal === FAULT_STATUS_CHANGE_MODAL}
          onRequestClose={closeModal}
        >
          <ModalFormBatchFaultStatusChange
            project={project}
            event={event}
            defaultFaultStatus={currentFaultStatus}
            period={period}
            analysesGroupedByFaultStatus={analysesGroupedByFaultStatus}
            totalAnalysesCount={totalAnalysesCount}
            handleOnSubmit={handleBatchFaultStatusChange}
            closeModal={closeModal}
          />
        </Modal>
      )}
    </>
  );
};

export default BatchFaultStatusChange;

// #region CSS
const batchFaultStatusChange = css`
  overflow: hidden;
`;

const title = css`
  display: inline-flex;
  align-items: center;
  gap: ${spacings.xxs};
`;

const content = css`
  margin-top: ${spacings.xxs};
  margin-left: 20px;
`;

const description = css`
  color: ${color.carbon800};
`;

const actionFaultValueStatus = css`
  margin-top: ${spacings.xs};
  display: flex;
  flex-wrap: wrap;
  gap: ${spacings.m};
`;

const no_detection = css`
  color: ${color.carbon800};
`;
// #endregion CSS
